import aa from 'search-insights'
import { global } from './constants'
import { filter, find, intersection, mapObject, values } from 'underscore'
import ActorIcon from './ActorIcon'
import clientImage from './client-avatar.svg'
import lockImage from './lock-icon.svg'
import { routeForRole, openProfilePage } from './utils'
import React, { useEffect, useRef } from 'react'
import { Tooltip } from 'bootstrap'

const savePersonClicked = person => {
  const maxSuggestions = 15

  let people = JSON.parse(localStorage.getItem(`people_searched_${global.organizationId}`))

  if (people) {
    let peopleSet = new Set(people)
    peopleSet.add(person.id)

    people = Array.from(peopleSet)
  } else {
    people = [person.id]
  }

  // limit to maxSuggestions most recent searches
  if (people.length > maxSuggestions) {
    people = people.slice(people.length - maxSuggestions)
  }

  localStorage.setItem(`people_searched_${global.organizationId}`, JSON.stringify(people))
}

export const SearchResultItem = ({
  person,
  personalInfo,
  position,
  adminSiteIds,
  usesAdvancedSiteManagement,
  onClick
}) => {
  aa('init', { appId: global.applicationId, apiKey: global.searchKey }) // initializing search insights library for click tracking

  const handleOnClick = (event, role, position, person, forceNewWindow = false) => {
    savePersonClicked(person)

    return onClick
      ? onClick(event, role, position, person, forceNewWindow)
      : openProfilePage(event, role, position, person, forceNewWindow)
  }

  const capitalizeRole = roleName => {
    return roleName.charAt(0).toUpperCase() + roleName.slice(1)
  }

  const notActiveOrApproved = role => {
    if (role.status !== 'active' && role.status !== 'approved') {
      return 'inactive'
    } else {
      return ''
    }
  }

  // helper function bolds results text matching input text
  const highlight = (person, attributeName) => {
    let attr = person._highlightResult[attributeName]

    if (attr && attr.matchLevel !== 'none') {
      return (
        <span>
          <span className='visually-hidden'>{person[attributeName]}</span>
          <span aria-hidden dangerouslySetInnerHTML={{ __html: `${attr.value}` }} />
        </span>
      )
    } else if (attr) {
      return <span>{attr.value}</span>
    } else {
      // for when person._highlightResult[attribute] is undefined for an attribute (ex. caregiver name)
      return <span>{person[attributeName]}</span>
    }
  }

  const displayDbaOrName = person => {
    if (person.dba) {
      return <span className='dba'>{highlight(person, 'dba')}</span>
    } else {
      return (
        <span className='name'>
          {highlight(person, 'first_name')} {person.preferred_name && <q>{highlight(person, 'preferred_name')}</q>}
          {person.preferred_name && ' '}
          {person.middle_name && highlight(person, 'middle_name')} {highlight(person, 'last_name')}
        </span>
      )
    }
  }

  const createRolesArr = roles => {
    // adding a 'klass' key-val pair to the value for each person.roles object
    let rolesObj = mapObject(roles, function (roleDetailsValue, roleNameKey) {
      return { ...roleDetailsValue, klass: capitalizeRole(roleNameKey) }
    })

    // creating an array of objects (instead of one giant object with nested objects) to map over
    let rolesArr = []
    for (const role in rolesObj) {
      rolesArr.push({ [role]: rolesObj[role] })
    }
    return [
      filter(rolesArr, role => {
        return global.rolePriorityOrder.includes(values(role)[0].klass)
      }),
      rolesArr // return original formated array
    ]
  }

  const rolePills = (roles, onClickCallback) => {
    const sortedRoles = roles.sort((a, b) => global.rolePriorityOrder.indexOf(a) - global.rolePriorityOrder.indexOf(b))

    const firstTwoRoles = sortedRoles.slice(0, 2)
    const remaining = sortedRoles.slice(2)

    const pills = firstTwoRoles.map((r, idx) => {
      return (
        <li
          className={`badge badge-pill custom-badge ms-0 me-1 ${notActiveOrApproved(values(r)[0])}`}
          onClick={event => onClickCallback(event, values(r)[0], position, person)}
          key={idx}
        >
          {capitalizeRole(values(r)[0].klass)}
        </li>
      )
    })

    if (remaining.length === 0) return pills

    return (
      <>
        {pills} <span className={'fw-medium text-black align-content-center'}>+{remaining.length}</span>
      </>
    )
  }

  const buildAddressString = (city, state) => {
    if (!city) return '-'
    if (!state) return city

    return `${city}, ${state}`
  }

  const ContactInfo = ({ type, contacts, personalInfo }) => {
    const forceAnonymous = personalInfo.force_anonymous_contacts
    const tooltipRef = useRef()

    useEffect(() => {
      if (tooltipRef.current && !forceAnonymous && contacts.length > 1) {
        const unavailable = contacts.filter(contact => !contact.name).length
        let tooltipText = contacts.filter(contact => contact.name).map(contact => contact.name)

        if (unavailable > 0) {
          tooltipText.push(`+${unavailable} ${unavailable === 1 ? 'client' : 'clients'}`)
        }

        new Tooltip(tooltipRef.current, {
          html: true,
          title: tooltipText.join('<br/>')
        })
      }
    }, [tooltipRef, forceAnonymous])

    if (contacts.length === 0) return null
    const contact = contacts[0]

    if (forceAnonymous || contacts.length > 1 || !contact.permission) {
      return (
        <span>
          {type} for{' '}
          <a
            href={personalInfo.contacts_path}
            className={'ui--link link--secondary'}
            target={'_top'}
            ref={tooltipRef}
          >
            {`${contacts.length} ${contacts.length === 1 ? 'client' : 'clients'}`}
          </a>
        </span>
      )
    }

    return (
      <span>
        {type} for{' '}
        <a href={contact.url} className={'grey-dotted-bottom-line text-grey'}>
          {contact.name}
        </a>
      </span>
    )
  }

  const caregiverContactProfessionalInfo = personalInfo => {
    const professionalInfo = personalInfo.professional_info
    const contacts = personalInfo.contacts || []
    const recipients = personalInfo.care_recipients || []

    if (!professionalInfo && contacts.length === 0 && recipients.length === 0) {
      return
    }

    return (
      <div className={'d-flex flex-column'}>
        {professionalInfo}
        <ContactInfo type={'Caregiver'} contacts={recipients} personalInfo={personalInfo}></ContactInfo>
        <ContactInfo type={'Contact'} contacts={contacts} personalInfo={personalInfo}></ContactInfo>
      </div>
    )
  }

  const Row = ({ person, personalInfo, position, onClickCallback }) => {
    const tooltipRef = useRef()

    const tooltipText =
      'You only have limited access to this client, because they are not assigned to any of your Sites'
    useEffect(() => {
      if (tooltipRef.current) {
        new Tooltip(tooltipRef.current, {
          html: true,
          title: tooltipText
        })
      }
    }, [tooltipRef])

    let [rolesArr, allRoles] = createRolesArr(person.roles) // formatting person.roles to simplify iteration
    const volunteer = find(rolesArr, role => values(role)[0].klass === 'Volunteer')

    // re-order roles to prioritize client if present
    const customerIdx = rolesArr.findIndex(role => values(role)[0].klass === 'Client')
    if (customerIdx !== -1) {
      rolesArr = rolesArr.concat(rolesArr.splice(customerIdx, 1))
    }

    const client = find(allRoles, role => values(role)[0].klass === 'Client' || values(role)[0].klass === 'Caller')
    const adminAdministeredClientSites = intersection(adminSiteIds, person.display_site_ids)
    const limitedAccess = client && usesAdvancedSiteManagement && adminAdministeredClientSites.length === 0

    // render last role first to allow for styling sibling roles (badges) when there are 2+ roles (badges) per row
    // see corresponding stylesheet or this link for more: https://stackoverflow.com/a/36118012
    const primaryRole = rolesArr[rolesArr.length - 1]

    let addressString = buildAddressString(person.display_city || person.display_county, personalInfo?.state)

    const contactDetailsView = personalInfo && caregiverContactProfessionalInfo(personalInfo)
    const firstRole = values(primaryRole)[0]
    const handleRowClick = event => onClickCallback(event, firstRole, position, person)

    return (
      <div role='row' className='row search-result-row'>
        <div
          role='rowheader'
          className='col-xl-2 my-1 my-xl-0 d-flex flex-row align-items-center'
          id={`person_${person.id}`}
          onClick={handleRowClick}
        >
          {volunteer ? <ActorIcon actor={person} /> : <img src={clientImage} className={'client-icon'} alt='' />}
          <div className='d-flex flex-column'>
            {displayDbaOrName(person)}
            {person.client_slug && <span>id: {highlight(person, 'client_slug')}</span>}
            {person.caregiver_first_name && (
              <span className='caregiver'>
                Primary Contact: {highlight(person, 'caregiver_first_name')}{' '}
                {person.caregiver_preferred_name && highlight(person, 'caregiver_preferred_name')}{' '}
                {highlight(person, 'caregiver_last_name')}
              </span>
            )}
          </div>
        </div>
        <div role='cell' className='col-xl-1 my-1 my-xl-0 d-flex flex-row align-items-center' onClick={handleRowClick}>
          <span>
            {person.date_of_birth ? highlight(person, 'date_of_birth') : '-'}
            {personalInfo?.age && `, age: ${personalInfo.age}`}
          </span>
        </div>
        <div role='cell' className='col-xl-1 my-1 my-xl-0' onClick={handleRowClick}>
          {addressString}
        </div>
        <div role='cell' className='col-xl-2 my-1 my-xl-0 d-flex flex-column' onClick={handleRowClick}>
          {person.email ? highlight(person, 'email') : '-'}
          <span>{person.display_phone_numbers || '-'}</span>
        </div>
        <div
          role='cell'
          className='col-xl-3 my-1 my-xl-0 filled-empty-div'
          onClick={rolesArr.length > 0 ? handleRowClick : undefined}
        >
          <ul>{rolePills(rolesArr, onClickCallback)}</ul>
        </div>
        <div
          role='cell'
          className='col-xl-2 my-1 my-xl-0 filled-empty-div'
          onClick={!contactDetailsView ? handleRowClick : undefined}
        >
          {contactDetailsView}
        </div>
        <div role='cell' className='col-xl-1 my-1 my-xl-0 d-flex flex-row' onClick={handleRowClick}>
          {/* set proper href for a11y purposes even though default will be prevented */}
          <a
            href={routeForRole(firstRole, person)}
            target='_blank'
            className={'new-window-icon'}
            onClick={event => openProfilePage(event, firstRole, position, person, true)}
          >
            <i className='fa-solid fa-arrow-up-long fa-rotate-by fa-lg' style={{ '--fa-rotate-angle': '45deg' }}></i>
            <span className='visually-hidden'>Open profile in new window</span>
          </a>
          {usesAdvancedSiteManagement && limitedAccess && (
            <span className={'ps-4'} style={{ paddingTop: '0.125rem' }} ref={tooltipRef}>
              <img src={lockImage} className='lock-icon' alt={tooltipText} />
            </span>
          )}
          <button type='button' className='visually-hidden' onClick={handleRowClick}>
            Select this person
          </button>
        </div>
      </div>
    )
  }

  return <Row person={person} personalInfo={personalInfo} position={position} onClickCallback={handleOnClick} />
}
